import { createApi, fetchBaseQuery } from "@reduxjs/toolkit/query/react";

export const RolesPermissionsApi = createApi({
	reducerPath: "RolesPermissions",
	baseQuery: fetchBaseQuery({
		baseUrl: `${process.env.REACT_APP_BACKEND2_URL}/v1`,
		prepareHeaders: (headers) => {
			const token = localStorage.getItem("token");
			if (token) {
				headers.set("Authorization", `Bearer ${token}`);
			}
			return headers;
		},
	}),
	endpoints: (builder) => ({
		getAllPermissions: builder.query({
			query: (val) => {
				const page = val.page ?? 1;
				const limit = val.limit ?? 10;
				const sortBy = val.sortby ? `&sortBy=${val.sortby}` : "";
				const order = val.order ? `&sortOrder=${val.order}` : "";
				const search = val.search ? `&search=${val.search}` : "";
				const queryString =
					`${
						val.queryString[0] === "&" ? val.queryString : `&${val.queryString}`
					}` || "";
				return `/permissions?page=${page}&limit=${limit}${search}${sortBy}${order}${queryString}`;
			},
		}),
		getSpecificPermission: builder.query({
			query: (id) => `/permissions/${id}`,
		}),
		addPermission: builder.mutation({
			query: (val) => ({
				url: `/permissions`,
				method: "POST",
				body: val,
			}),
		}),
		editPermission: builder.mutation({
			query: ({ val, id }) => ({
				url: `/permissions/${id}`,
				method: "PATCH",
				body: val,
			}),
		}),
		deletePermission: builder.mutation({
			query: (id) => ({
				url: `/permissions/${id}`,
				method: "DELETE",
			}),
		}),
		getAllRoles: builder.query({
			query: (val) => {
				const page = val.page ?? 1;
				const limit = val.limit ?? 10;
				const sortBy = val.sortby ? `&sortBy=${val.sortby}` : "";
				const order = val.order ? `&sortOrder=${val.order}` : "";
				const search = val.search ? `&search=${val.search}` : "";
				const queryString = val.queryString
					? `&${val.queryString.substring(1)}`
					: "";
				return `/roles?page=${page}&limit=${limit}${search}${sortBy}${order}${queryString}`;
			},
		}),
		getSpecificRoles: builder.query({
			query: (id) => `/roles/${id}`,
		}),
		addRole: builder.mutation({
			query: (val) => ({
				url: `/roles`,
				method: "POST",
				body: val,
			}),
		}),
		editRole: builder.mutation({
			query: ({ val, id }) => ({
				url: `/roles/${id}`,
				method: "PATCH",
				body: val,
			}),
		}),
		deleteRole: builder.mutation({
			query: (id) => ({
				url: `/roles/${id}`,
				method: "DELETE",
			}),
		}),
		getRolesUsers: builder.query({
			query: (val) => {
				const page = val.page ?? 1;
				const limit = val.limit ?? 10;
				const id = val.id;
				const sortBy = val.sortby ? `&sortBy=${val.sortby}` : "";
				const order = val.order ? `&sortOrder=${val.order}` : "";
				const search = val.search ? `&search=${val.search}` : "";
				return `/roles/${id}/users?page=${page}&limit=${limit}${search}${sortBy}${order}`;
			},
		}),
		getModules: builder.query({
			query: () => "/permissions/enums/modules",
		}),
		getSubModules: builder.query({
			query: ({ module }) => `/permissions/enums/submodules?module=${module}`,
		}),
		getResources: builder.query({
			query: ({ submodule }) =>
				`/permissions/enums/resources?submodule=${submodule}`,
		}),
		getAction: builder.query({
			query: ({ resource }) =>
				`/permissions/enums/actions?resource=${resource}`,
		}),
		getUserRole: builder.query({
			query: ({ id }) => `/roles/user/${id}?limit=100000&page=1`,
		}),
		assignRole: builder.mutation({
			query: ({ userId, roleId }) => ({
				url: `/roles/assign-user`,
				method: "POST",
				body: {
					userId,
					roleId,
				},
			}),
		}),

		removeRole: builder.mutation({
			query: ({ id }) => ({
				url: `/roles/remove-user/${id}`,
				method: "DELETE",
			}),
		}),
		getUserAuth: builder.query({
			query: () => `/permissions/frontend-format`,
		}),
		getUnAssignedUserRole: builder.query({
			query: () => `/roles/users/unAssigned`,
		}),
		getMyPermissions: builder.query({
			query: ({ userType }) =>
				`/permissions/my-permissions?userType=${userType}`,
		}),
	}),
});

export const {
	useGetAllPermissionsQuery,
	useGetSpecificPermissionQuery,
	useAddPermissionMutation,
	useEditPermissionMutation,
	useDeletePermissionMutation,
	useGetAllRolesQuery,
	useGetSpecificRolesQuery,
	useAddRoleMutation,
	useEditRoleMutation,
	useDeleteRoleMutation,
	useGetRolesUsersQuery,
	useGetModulesQuery,
	useGetSubModulesQuery,
	useGetResourcesQuery,
	useGetActionQuery,
	useGetUserRoleQuery,
	useAssignRoleMutation,
	useRemoveRoleMutation,
	useLazyGetUserAuthQuery,
	useGetUnAssignedUserRoleQuery,
	useGetMyPermissionsQuery,
} = RolesPermissionsApi;
